import { Suggestion, Token } from './types';
import { FilterTokenizer } from './FilterTokenizer';

export interface InsertionResult {
  newValue: string;
  newCursorPosition: number;
  shouldKeepOpen: boolean;
}

/**
 * Handles the complex logic of inserting a suggestion into the text
 */
export function insertSuggestion(
  suggestion: Suggestion,
  currentValue: string,
  cursorPosition: number,
  currentToken: Token | null,
  cursorContext: {
    insideToken: boolean;
    tokenIndex: number;
    positionInToken: number;
  } | null
): InsertionResult {
  // Don't insert hints
  if (suggestion.isHint) {
    return {
      newValue: currentValue,
      newCursorPosition: cursorPosition,
      shouldKeepOpen: false,
    };
  }

  let newValue = currentValue;
  let insertValue = suggestion.value;
  let newCursorPosition = cursorPosition;

  // Handle semantic operators with cursor positioning
  if (suggestion.type === 'operator' && insertValue.includes('|')) {
    const cursorIndex = insertValue.indexOf('|');
    insertValue = insertValue.replace('|', '');

    if (currentToken && cursorContext?.insideToken) {
      // Replace the current token
      newValue =
        currentValue.slice(0, currentToken.start) +
        insertValue +
        currentValue.slice(currentToken.end);
      newCursorPosition = currentToken.start + cursorIndex;
    } else {
      // Insert at cursor position
      const beforeCursor = currentValue.slice(0, cursorPosition);
      const afterCursor = currentValue.slice(cursorPosition);

      const needsSpaceBefore =
        beforeCursor.length > 0 &&
        !beforeCursor.endsWith(' ') &&
        !beforeCursor.endsWith('(');

      const fullInsert = (needsSpaceBefore ? ' ' : '') + insertValue;
      newValue = beforeCursor + fullInsert + afterCursor;
      newCursorPosition =
        beforeCursor.length + (needsSpaceBefore ? 1 : 0) + cursorIndex;
    }

    return {
      newValue,
      newCursorPosition,
      shouldKeepOpen: false,
    };
  }

  // Add quotes for all values
  if (suggestion.type === 'value') {
    insertValue = `"${suggestion.value}"`;
  }

  // Check if we're replacing an incomplete quoted value
  const quoteContent = FilterTokenizer.extractIncompleteQuoteContent(
    currentValue,
    cursorPosition
  );
  const isInIncompleteQuote =
    !currentToken && suggestion.type === 'value' && quoteContent !== null;

  if (isInIncompleteQuote) {
    // Find the last unclosed quote and replace everything after it
    const textBeforeCursor = currentValue.substring(0, cursorPosition);
    const quoteContext = FilterTokenizer.getQuoteContext(textBeforeCursor);

    if (quoteContext.quoteStartIndex >= 0) {
      // Replace from the quote onwards
      const beforeQuote = currentValue.substring(
        0,
        quoteContext.quoteStartIndex
      );
      const afterCursor = currentValue.substring(cursorPosition);

      newValue = beforeQuote + insertValue + afterCursor;
      newCursorPosition = beforeQuote.length + insertValue.length;
    }
  } else if (currentToken && cursorContext?.insideToken) {
    // Replace the current token
    newValue =
      currentValue.slice(0, currentToken.start) +
      insertValue +
      currentValue.slice(currentToken.end);
    newCursorPosition = currentToken.start + insertValue.length;
  } else {
    // Insert at cursor position
    const beforeCursor = currentValue.slice(0, cursorPosition);
    const afterCursor = currentValue.slice(cursorPosition);

    // Add space before if needed
    const needsSpaceBefore =
      beforeCursor.length > 0 &&
      !beforeCursor.endsWith(' ') &&
      !beforeCursor.endsWith('(');

    newValue =
      beforeCursor + (needsSpaceBefore ? ' ' : '') + insertValue + afterCursor;

    newCursorPosition =
      beforeCursor.length + (needsSpaceBefore ? 1 : 0) + insertValue.length;
  }

  // Add trailing space for non-value suggestions
  if (suggestion.type !== 'value' && !newValue.endsWith(' ')) {
    newValue = newValue.trimEnd() + ' ';
    newCursorPosition = newValue.length;
  }

  // Determine if we should keep autocomplete open
  const shouldKeepOpen = suggestion.type !== 'value';

  return {
    newValue,
    newCursorPosition,
    shouldKeepOpen,
  };
}

/**
 * Calculate dynamic height for suggestions dropdown
 */
export function calculateDropdownHeight(numItems: number): number {
  const ITEM_HEIGHT = 32;
  const MAX_ITEMS = 5;
  const MIN_HEIGHT = 100;

  if (numItems === 0) {
    return MIN_HEIGHT;
  }

  // Show max 5 items, then scroll
  const visibleItems = Math.min(numItems, MAX_ITEMS);
  return Math.max(MIN_HEIGHT, visibleItems * ITEM_HEIGHT + 20);
}

/**
 * Get suggestion type badge styles
 */
export function getSuggestionTypeStyles(type: string): string {
  switch (type) {
    case 'field':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
    case 'operator':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
    case 'value':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
    case 'logical':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
  }
}

/**
 * Determine header text and empty text based on suggestions
 */
export function getSuggestionDisplayInfo(
  suggestions: Suggestion[],
  currentToken: Token | null,
  expectedNext: string,
  value: string,
  messages: any
): {
  isShowingHints: boolean;
  headerText: string;
  emptyText: string;
} {
  const realSuggestions = suggestions.filter((s) => !s.isHint);
  const hints = suggestions.filter((s) => s.isHint);

  const hasRealSuggestions = realSuggestions.length > 0;
  const hasHints = hints.length > 0;

  let headerText: string;
  let emptyText: string;
  let isShowingHints: boolean;

  if (hasRealSuggestions) {
    headerText = `${messages.filterAutoCompleteSuggestions || 'Suggestions'} (${
      realSuggestions.length
    })`;
    emptyText =
      messages.filterAutoCompleteNoSuggestions || 'No suggestions available';
    isShowingHints = false;
  } else if (hasHints) {
    headerText = `${messages.filterAutoCompleteHints || 'Input Hints'} (${
      hints.length
    })`;
    emptyText =
      messages.filterAutoCompleteNoHints || 'No input hints available';
    isShowingHints = true;
  } else {
    const isActivelySearching = currentToken && currentToken.type === 'value';
    if (
      isActivelySearching ||
      (expectedNext === 'Value' && value.includes('"'))
    ) {
      headerText = messages.filterAutoCompleteNoMatches || 'No matches found';
      emptyText =
        messages.filterAutoCompleteNoMatchesDescription ||
        'No suggestions match your search. Try a different term.';
      isShowingHints = false;
    } else {
      headerText =
        messages.filterAutoCompleteNoSuggestions || 'No suggestions available';
      emptyText =
        messages.filterAutoCompleteNoSuggestions || 'No suggestions available';
      isShowingHints = false;
    }
  }

  return { isShowingHints, headerText, emptyText };
}
